import { useEffect, useState } from "react";
import EditButton from "./EditButton";
import Input from "./Input";
import { isoToJalali } from "../../../helper/isoToJalali";
import SVG from "../../../Components/SVG";
import { API_BASE_URL } from "../../../config/api";

export default function InformationCard({ accountData }) {
  const isValidNationalIdFormat = (id) => {
    if (!id) return true;
    return /^[0-9۰-۹]{10}$/.test(id);
  };
  const isValidEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const isValidPersianPhone = (phone) => {
    if (!phone) return true;
    const persianDigits = "۰۱۲۳۴۵۶۷۸۹";

    // convert Persian digits → English
    const english = phone.replace(/[۰-۹]/g, (d) => persianDigits.indexOf(d));

    // must match: 09XXXXXXXXX (11 digits)
    return /^09\d{9}$/.test(english);
  };
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editEmail, setEditEmail] = useState(localStorage.getItem("email"));
  const [validation, setValidation] = useState({
    national_id: true,
    email: true,
    phone_number: true,
  });
  useEffect(() => {
    if (accountData && !isEditing) {
      setEditData({ ...accountData });
    }
  }, [accountData, isEditing]);

  useEffect(() => {
    if (editData && editEmail) {
      if (!isValidNationalIdFormat(editData?.national_id)) {
        setValidation((pre) => ({ ...pre, national_id: false }));
      } else {
        setValidation((pre) => ({ ...pre, national_id: true }));
      }
      if (!isValidEmail(editEmail)) {
        setValidation((pre) => ({ ...pre, email: false }));
      } else {
        setValidation((pre) => ({ ...pre, email: true }));
      }
      if (!isValidPersianPhone(editData?.phone_number)) {
        setValidation((pre) => ({ ...pre, phone_number: false }));
      } else {
        setValidation((pre) => ({ ...pre, phone_number: true }));
      }
    }
  }, [isEditing, editData, editEmail]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditEmail(localStorage.getItem("email"));
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (
      !validation.email ||
      !validation.national_id ||
      !validation.phone_number
    ) {
      return;
    }
    // Here you could add save logic (API call, state update, etc.)
    try {
      let response = await fetch(
        `${API_BASE_URL}/api/v0/auth/admin/info/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify({
            fn: editData?.fn,
            ln: editData?.ln,
            national_id: editData?.national_id,
            birth_date: editData?.birth_date,
            phone_number: editData?.phone_number,
          }),
        },
      );

      // If access token is invalid/expired → try refresh
      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          // refresh failed → logout
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          window.location.href = "/signin";
          return; // ⬅️ IMPORTANT
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        // retry original request with new access token
        response = await fetch(
          `${API_BASE_URL}/api/v0/auth/admin/info/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
            body: JSON.stringify({
              fn: editData?.fn,
              ln: editData?.ln,
              national_id: editData?.national_id,
              birth_date: editData?.birth_date,
              phone_number: editData?.phone_number,
            }),
          },
        );
      }

      // Now handle final response (either first or retried)
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Account Data:", data);
      if (editEmail !== localStorage.getItem("email")) {
        try {
          let response = await fetch(
            `${API_BASE_URL}/api/v0/auth/trader/update-email`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("access")}`,
              },
              body: JSON.stringify({
                email: editEmail,
              }),
            },
          );

          // If access token is invalid/expired → try refresh
          if (response.status === 401) {
            const refreshResponse = await fetch(
              `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  refresh: localStorage.getItem("refresh"),
                }),
              },
            );

            if (!refreshResponse.ok) {
              // refresh failed → logout
              localStorage.removeItem("access");
              localStorage.removeItem("authCredentials");
              localStorage.removeItem("refresh");
              localStorage.removeItem("role");
              localStorage.removeItem("email");
              window.location.href = "/signin";
              return; // ⬅️ IMPORTANT
            }

            const refreshData = await refreshResponse.json();
            localStorage.setItem("access", refreshData.access);

            // retry original request with new access token
            response = await fetch(
              `${API_BASE_URL}/api/v0/auth/trader/update-email`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${localStorage.getItem("access")}`,
                },
                body: JSON.stringify({
                  email: editEmail,
                }),
              },
            );
          }

          // Now handle final response (either first or retried)
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          localStorage.setItem("email", editEmail);
          const data = await response.json();
          console.log("Account Data:", data);
        } catch (error) {
          console.error("Error fetching account data:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
    window.location.reload();
  };

  return (
    <div className="w-full max-w-3xl rounded-2xl p-4 flex flex-col gap-6 bg-zi-bg2-light dark:bg-zi-bg2-dark">
      <div className="w-full flex items-start justify-between">
        <SVG
          name="profilePicture"
          classname="size-16 stroke-zi-text-light dark:stroke-zi-text-dark h-fill aspect-square"
        />
        <span className="font-medium text-xl text-zi-text-light dark:text-zi-text-dark">
          اطلاعات حساب کاربری مدیر سامانه
        </span>
        <EditButton
          isEditing={isEditing}
          onEdit={handleEdit}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      </div>

      {/* <div className="w-full flex flex-col gap-1">
        <span className="text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
          نقش مدیریتی
        </span>
        <span className="text-base text-zi-text-light dark:text-zi-text-dark">
          ادمین ردیف اول
        </span>
      </div> */}

      <form
        className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6"
        onSubmit={handleSave}
      >
        <Input
          label="نام"
          inputSample={accountData?.fn || ""}
          value={editData?.fn}
          type="text"
          id="fn"
          name="fn"
          isReadOnly={!isEditing || localStorage.getItem("role") === "trader"}
          isAuotComplete={true}
          onChange={(e) =>
            setEditData((pre) => ({ ...pre, fn: e.target.value }))
          }
        />
        <Input
          label="نام خانوادگی"
          inputSample={accountData?.ln || ""}
          value={editData?.ln || ""}
          type="text"
          id="ln"
          name="ln"
          isReadOnly={!isEditing || localStorage.getItem("role") === "trader"}
          isAuotComplete={true}
          onChange={(e) =>
            setEditData((pre) => ({ ...pre, ln: e.target.value }))
          }
        />
        <Input
          label="آدرس ایمیل"
          inputSample={localStorage.getItem("email")}
          value={editEmail || ""}
          type="email"
          id="email"
          name="email"
          isReadOnly={!isEditing}
          isAuotComplete={true}
          onChange={(e) => {
            setEditEmail(e.target.value);
          }}
        />
        <Input
          label="شماره تلفن همراه"
          inputSample={accountData?.phone_number || ""}
          value={editData?.phone_number || ""}
          type="tel"
          id="tel"
          name="tel"
          isReadOnly={!isEditing}
          isAuotComplete={true}
          isLTR={true}
          onChange={(e) =>
            setEditData((pre) => ({ ...pre, phone_number: e.target.value }))
          }
        />
      </form>
      {!validation.email && (
        <p className="text-red-500 text-sm">ایمیل وارد شده صحیح نمی باشد</p>
      )}
      {!validation.phone_number && (
        <p className="text-red-500 text-sm">
          شماره موبایل وارد شده صحیح نمی باشد
        </p>
      )}
      {!validation.national_id && (
        <p className="text-red-500 text-sm">کد ملی وارد شده صحیح نمی باشد</p>
      )}
    </div>
  );
}
